import { IconArrowDownBold } from '@croquiscom-pvt/zds';
import { Text } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';

import { PDPCustomAttributeKeys } from '@common/test-automation';

interface Props {
  display?: boolean;
  label?: string;
  open: boolean;
  placeholder: string;
  openAnimation?: boolean;
  children: React.ReactNode;
  handleClick: () => void;
  handleAnimationComplete?: () => void;
}

export const SelectBoxDropdown = ({
  children,
  display = true,
  open,
  label,
  placeholder,
  openAnimation = true,
  handleClick,
  handleAnimationComplete,
}: Props) => {
  const name = label || placeholder;

  if (!display) {
    return null;
  }

  return (
    <Container active={open}>
      <DisplayBox
        active={open}
        onClick={handleClick}
        data-allow-preview
        data-custom-ta-key={PDPCustomAttributeKeys.PDP_PRODUCT_OPTIONS_12}
      >
        <Text variant='Body_15_SemiBold' v3Color={open || label ? 'gray900' : 'gray300'}>
          {name.replace(/&nbsp;|&#160;/g, ' ')}
        </Text>
        <IconArrowDownBold
          size={20}
          v3Color={open || label ? 'gray900' : 'gray300'}
          style={open ? { transform: 'rotate(180deg)' } : {}}
        />
      </DisplayBox>
      {open && (
        <motion.div
          className='list-container'
          initial={openAnimation ? { opacity: 0, height: 0 } : {}}
          animate={{
            opacity: 1,
            height: 'auto',
            visibility: 'visible',
          }}
          exit={{
            opacity: 0,
            height: 0,
            visibility: 'hidden',
          }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          onAnimationComplete={handleAnimationComplete}
        >
          {children}
        </motion.div>
      )}
    </Container>
  );
};

const SELECT_BOX_BORDER_WIDTH = 2;

const Container = styled.div<{ active: boolean }>`
  margin-bottom: 8px;
  overflow: hidden;
  box-sizing: border-box;
  border: ${`${SELECT_BOX_BORDER_WIDTH}px`} solid ${vars.v3Color.gray150};
  border-radius: 8px;

  ${({ active }) =>
    active &&
    `
      border-color: ${vars.v3Color.gray200};
      opacity: 1;
    `}

  .list-container {
    & > div {
      border-top: 1px solid ${vars.v3Color.gray100};

      &:first-of-type {
        border-top: none;
      }
    }
  }
`;

const DisplayBox = styled.div<{ active: boolean }>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 48px;
  padding: 0 16px;
  box-sizing: border-box;
  border-bottom: 1px solid transparent;
  cursor: pointer;

  ${({ active }) => active && `border-color: ${vars.v3Color.gray200};`}
  span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    word-break: break-all;
  }
`;
